import React, { useMemo, useState } from 'react';

import styled from 'styled-components';

import { useTranslation } from 'react-i18next';

import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import dayjs from '@aqx-core/common/dayjs';
import { EThemeTemplate } from '@aqx-core/common/services/company/constants';
import { useCompanyCustomThemeTemplate, useCompanyName } from '@aqx-core/common/services/company/hooks';
import { EAppThemeMode } from '@aqx-core/common/theme';
import HantecLoginBackgroundImage from '@aqx-member/web/assets/hantec-login-bg.png';
import LoginBackgroundImage from '@aqx-member/web/assets/login-bg.png';
import UILanguageSelectPicker from '@aqx-member/web/components/language-select-picker';
import UICompanyLogo from '@aqx-member/web/components/logo';

const AuthContainer: FCC = ({ children }) => {
  const { t } = useTranslation();
  const [error, setError] = useState(false);

  const companyName = useCompanyName();

  const getCurrentYear = useMemo(() => {
    return dayjs().year();
  }, []);

  const { themeTemplate } = useCompanyCustomThemeTemplate();
  const backgroundImage = themeTemplate === EThemeTemplate.HANTEC ? HantecLoginBackgroundImage : LoginBackgroundImage;

  return (
    <StyledLoginScreen>
      <StyledBannerContainer $backgroundImage={backgroundImage}>
        <StyledUICompanyLogo onClick={() => setError(!error)}>
          <StyledUITextLogo typography={ETypography.DISPLAY_S_REGULAR}>
            {t('Begin your trading journey with')}
          </StyledUITextLogo>
          <UICompanyLogo allowedMode={[EAppThemeMode.DARK]} />
        </StyledUICompanyLogo>
      </StyledBannerContainer>
      <StyledLoginRightSection>
        <StyledLanguageSelectorContainer>
          <UILanguageSelectPicker />
        </StyledLanguageSelectorContainer>
        {children}

        <StyledFooterTextContainer>
          <StyledUITextFooter typography={ETypography.TEXT_L_REGULAR}>
            {`© ${companyName} ${getCurrentYear}. ${t('All rights reserved.')}`}
          </StyledUITextFooter>
        </StyledFooterTextContainer>
      </StyledLoginRightSection>
    </StyledLoginScreen>
  );
};

export default AuthContainer;

const StyledLoginScreen = styled.div`
  flex: 1;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.themeColors.background.default};

  @media all and (max-width: 1000px) {
    flex-direction: column;
  }
`;

const StyledUITextLogo = styled(UIText)`
  color: ${({ theme }) => theme.themeColors.white};
`;

const StyledBannerContainer = styled.div<{ $backgroundImage: string }>`
  width: 45%;
  height: 100vh;
  background-image: url(${({ $backgroundImage }) => $backgroundImage});
  background-size: cover;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;

  @media all and (max-width: 1000px) {
    height: 200px;
    width: 100%;
    justify-content: center;
  }
`;

const StyledUICompanyLogo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  margin-bottom: ${ESpacing.XS}px;
  max-width: 325px;
  gap: ${ESpacing.XXL}px;
`;

const StyledLoginRightSection = styled.div`
  height: 100dvh;
  overflow-y: auto;
  padding: 0px ${ESpacing.XXL}px;

  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.themeColors.background.default};
  @media all and (max-width: 1000px) {
    width: 100%;
  }
`;

const StyledFooterTextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${ESpacing.M}px ${ESpacing.XL}px;
`;

const StyledUITextFooter = styled(UIText)`
  color: ${({ theme }) => theme.themeColors.text.subtle};
`;

const StyledLanguageSelectorContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${ESpacing.M}px ${ESpacing.XL}px;
`;
